import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  signInWithRedirect,
  signUp,
  confirmSignUp,
  confirmSignIn,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { apiClient, SESSION_EXPIRED_EVENT } from '../api/client.js';
import { isSsoEnabled } from '../config/amplify.js';
import { SSO_WARNING_DISMISSED_KEY } from '../config/ssoWarning.js';
import { setSentryUser } from '../lib/sentry.js';
// local-dev-environment Phase 2 remediation (finding #5): getCurrentUser /
// fetchAuthSession / signIn / signOut route through the authClient seam
// (cognito | local via VITE_AUTH_MODE) instead of calling `aws-amplify/auth`
// directly. `signInWithRedirect` + `Hub` stay Amplify-direct — BanyanSSO
// federation is out of scope for local mode.
import { authClient } from './authClient.js';
import { suppressPostLoginCapture } from './postLoginPath.js';

/**
 * AuthContext keeps the legacy permissive `data?.profile ?? data` shape so that
 * a partial profile (e.g. user without a role yet — onboarding flow) does not
 * cause the entire auth state to collapse. The strict ProfileResponseSchema is
 * exposed via `@/api/schemas` for hooks that prefer typed parsing.
 */
type AuthProfile = Record<string, unknown> & {
  role?: unknown;
  opcoId?: unknown;
  opcoName?: unknown;
  status?: unknown;
  /**
   * TASK-097 / FEAT-067: server-derived nav-visibility flag (true when the user
   * is a member of ≥1 workshop instructor pool). Drives the "Teaching" nav area
   * only — individual session data is still authorized server-side on true
   * assignment. Set by GET /api/auth/profile.
   */
  isInstructor?: unknown;
  /**
   * Feature-flags task Phase 4: server-resolved `Record<FlagName, boolean>`
   * map from `resolveFlags(...)` (backend/src/lib/feature-flags.ts), added to
   * GET /api/auth/profile. Read via `useFlag(name)` (./useFlag.ts) rather
   * than directly off `profile.flags`.
   */
  flags?: unknown;
};

export interface AuthUser {
  username: string;
  email?: string | null;
  title?: string | null;
  opcoId?: string | null;
  [k: string]: string | number | boolean | null | undefined;
}

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True for an authenticated email/password session — the ID token has no
   * (or an empty) `identities` claim. False while loading, signed out, or for
   * BanyanSSO-federated sessions. Token-only: independent of the profile fetch.
   */
  isPasswordSession: boolean;
  profile: AuthProfile | null;
  role: string | null;
  isAdmin: boolean;
  isLeader: boolean;
  isCeo: boolean;
  isTechLeader: boolean;
  /** True when the user is an assigned-instructor pool member (FEAT-067). */
  isInstructor: boolean;
  /**
   * Server-resolved feature-flag map (feature-flags task Phase 4). Prefer
   * `useFlag(name)` (./useFlag.ts) over reading this directly. Defaults to
   * `{}` when the profile has no `flags` field (older backend, or profile
   * not yet loaded).
   */
  flags: Record<string, boolean>;
  oauthError: string | null;
  /**
   * Set when GET /api/auth/profile rejects with `federation_link_quarantined`
   * (403) — the backend refused to auto-link/auto-provision this BanyanSSO-
   * federated identity (unified-auth critique C1/C4). A dedicated, terminal
   * error distinct from `oauthError`: retrying the same federation would only
   * re-quarantine, so LoginPage renders a "contact your administrator" message
   * rather than the retryable federation-failure banner. The half-established
   * Cognito session is torn down (signOut) when this is set.
   */
  quarantineError: string | null;
  ssoEnabled: boolean;
  login: (email: string, password: string) => Promise<unknown>;
  completeNewPassword: (newPassword: string) => Promise<unknown>;
  register: (email: string, password: string, title: string) => Promise<unknown>;
  logout: () => Promise<void>;
  /**
   * Federate to BanyanSSO (Auth0 OIDC, wired as one Cognito external IdP).
   * `email` is passed as a best-effort `login_hint` — Cognito Hosted UI may
   * not forward it through to Auth0 (confirmed caveat; see
   * unified-auth-architecture.md Must-Confirm #3). When dropped, Auth0's
   * identifier-first Universal Login page is the accepted one-time fallback.
   */
  signInWithBanyanSSO: (email?: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<unknown>;
  forgotPassword: (email: string) => Promise<unknown>;
  forgotPasswordSubmit: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<unknown>;
  refreshProfile: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * User-facing message for a quarantined BanyanSSO-federated account. Kept
 * generic (no account-existence disclosure), mirroring the backend's
 * `federationLinkQuarantined` copy and `prevent_user_existence_errors`.
 */
const QUARANTINE_MESSAGE =
  'Your account needs review before you can sign in — please contact your administrator.';

/**
 * True when a failed /api/auth/profile fetch is the BanyanSSO quarantine 403.
 * The backend returns `{ error: 'federation_link_quarantined' }`, preserved on
 * the thrown ApiError's `body` (see api/client.ts → api/errors.ts).
 */
function isQuarantineError(err: unknown): boolean {
  const e = err as { body?: { error?: unknown } } | null | undefined;
  return e?.body?.error === 'federation_link_quarantined';
}

/**
 * Defensively coerce `profile.flags` into a plain `Record<string, boolean>`,
 * dropping any non-boolean values rather than throwing — an unexpected shape
 * from the backend must never break auth bootstrap. Feature-flags Phase 4.
 */
function coerceFlags(rawFlags: unknown): Record<string, boolean> {
  if (
    rawFlags === null ||
    typeof rawFlags !== 'object' ||
    Array.isArray(rawFlags)
  ) {
    return {};
  }
  const result: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(rawFlags as Record<string, unknown>)) {
    if (typeof value === 'boolean') {
      result[key] = value;
    }
  }
  return result;
}

/**
 * True when the ID token carries a federated-identity (`identities`) claim.
 * Mirrors backend `hasIdentitiesClaim` (backend/src/lib/auth.ts): a non-empty
 * value of any shape counts as federated, so FE and BE never disagree.
 */
function hasIdentitiesClaim(raw: unknown): boolean {
  if (raw == null) return false;
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return false;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.length > 0;
      return parsed != null;
    } catch {
      return true;
    }
  }
  return typeof raw === 'object';
}

function unwrapProfile(data: unknown): AuthProfile | null {
  if (data === null || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  const inner = obj.profile;
  if (inner && typeof inner === 'object') return inner as AuthProfile;
  return obj as AuthProfile;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordSession, setIsPasswordSession] = useState(false);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [quarantineError, setQuarantineError] = useState<string | null>(null);

  const [oauthPending, setOauthPending] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  });

  const loadUser = useCallback(async function loadUser() {
    // A fresh load attempt clears any prior terminal quarantine state — a
    // successful re-auth (different account) must not keep showing it.
    setQuarantineError(null);
    try {
      const currentUser = await authClient.getCurrentUser();
      const session = await authClient.fetchAuthSession();
      const idPayload = (session.tokens?.idToken?.payload ?? {}) as Record<
        string,
        unknown
      >;

      const attrs = {
        email: idPayload.email,
        'custom:title': idPayload['custom:title'],
        'custom:opco_id': idPayload['custom:opco_id'],
      } as Record<string, unknown>;

      const userObj: AuthUser = {
        username: currentUser.username,
        email: typeof attrs.email === 'string' ? attrs.email : null,
        title:
          typeof attrs['custom:title'] === 'string'
            ? (attrs['custom:title'] as string)
            : null,
        opcoId:
          typeof attrs['custom:opco_id'] === 'string'
            ? (attrs['custom:opco_id'] as string)
            : null,
      };
      for (const [k, v] of Object.entries(attrs)) {
        if (
          typeof v === 'string' ||
          typeof v === 'number' ||
          typeof v === 'boolean'
        ) {
          userObj[k] = v;
        }
      }

      setUser(userObj);
      setIsAuthenticated(true);
      setIsPasswordSession(!hasIdentitiesClaim(idPayload.identities));

      try {
        const profileData = await apiClient.get('/api/auth/profile');
        setProfile(unwrapProfile(profileData));
      } catch (profileErr) {
        if (isQuarantineError(profileErr)) {
          // BanyanSSO quarantine (403): the backend refused to auto-link /
          // auto-provision this federated identity. Do NOT leave the user in a
          // broken authenticated-but-profileless state — tear the half-
          // established Cognito session back down and drive a dedicated,
          // terminal error state that LoginPage renders (mirrors how the
          // oauthError federation-failure path clears + surfaces).
          try {
            await authClient.signOut();
          } catch {
            /* ignore — nothing to clear */
          }
          setUser(null);
          setIsAuthenticated(false);
          setIsPasswordSession(false);
          setProfile(null);
          setQuarantineError(QUARANTINE_MESSAGE);
        } else {
          // Transient/unrelated profile failure — keep the permissive legacy
          // behavior (authenticated session, null profile).
          setProfile(null);
        }
      }
    } catch (err) {
      const e = err as { message?: string; name?: string } | null | undefined;
      const msg = (e?.message ?? '') + (e?.name ?? '');
      if (
        msg.includes('NotAuthorized') ||
        msg.includes('required scopes') ||
        msg.includes('oauth') ||
        msg.includes('There is already a signed in user')
      ) {
        try {
          await authClient.signOut();
        } catch {
          /* ignore */
        }
      }
      setUser(null);
      setIsAuthenticated(false);
      setIsPasswordSession(false);
      setProfile(null);
    } finally {
      if (!oauthPending) {
        setIsLoading(false);
      }
    }
  }, [oauthPending]);

  useEffect(() => {
    setSentryUser(user?.username ?? null);
  }, [user?.username]);

  useEffect(() => {
    loadUser();

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        setOauthError(null);
        setOauthPending(false);
        window.history.replaceState({}, '', window.location.pathname);
        await loadUser();
        setIsLoading(false);
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setOauthPending(false);
        setOauthError("Sign in didn't complete. Please try again.");
        setIsLoading(false);
        // AC-ERROR-2: clear any half-established session left by the failed
        // federation round-trip so no corrupt/partial state persists.
        authClient.signOut().catch(() => {
          /* ignore — nothing to clear */
        });
      }
    });

    function handleSessionExpired() {
      authClient.signOut()
        .catch(() => {})
        .finally(() => {
          setUser(null);
          setIsAuthenticated(false);
          setIsPasswordSession(false);
          setProfile(null);
          window.location.href = '/login?expired=1';
        });
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      unsubscribe();
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [loadUser]);

  async function login(email: string, password: string) {
    const normalizedEmail = email.trim().toLowerCase();
    const result = await authClient.signIn({
      username: normalizedEmail,
      password,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'PASSWORD' },
    });
    if (
      result.nextStep?.signInStep ===
      'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      return result;
    }
    await loadUser();
    return result;
  }

  async function completeNewPassword(newPassword: string) {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    await loadUser();
    return result;
  }

  async function register(email: string, password: string, title: string) {
    const normalizedEmail = email.trim().toLowerCase();
    return signUp({
      username: normalizedEmail,
      password,
      options: {
        userAttributes: {
          email: normalizedEmail,
          'custom:title': title,
        },
      },
    });
  }

  async function confirmSignUpFn(email: string, code: string) {
    return confirmSignUp({
      username: email.trim().toLowerCase(),
      confirmationCode: code,
    });
  }

  async function forgotPassword(email: string) {
    return apiClient.post('/api/auth/recover-access', {
      email: email.trim().toLowerCase(),
    });
  }

  async function forgotPasswordSubmit(
    email: string,
    code: string,
    newPassword: string
  ) {
    return confirmResetPassword({
      username: email.trim().toLowerCase(),
      confirmationCode: code,
      newPassword,
    });
  }

  async function signInWithBanyanSSO(email?: string) {
    setOauthError(null);
    try {
      await authClient.signOut();
    } catch {
      // Ignore — no session to clear
    }
    const redirectInput: Parameters<typeof signInWithRedirect>[0] = {
      provider: { custom: 'BanyanSSO' },
    };
    if (email) {
      // login_hint via Amplify's typed `options.loginHint` — Amplify forwards
      // it to Cognito's Hosted UI as the `login_hint` authorize param, so the
      // email-first hint reaches Cognito reliably (the earlier top-level
      // snake_case field was silently dropped by Amplify). Note the caveat
      // still applies one hop further out: Cognito is not guaranteed to
      // forward `login_hint` to the downstream Auth0/BanyanSSO OIDC provider,
      // so a re-prompt at the IdP is still tolerated.
      redirectInput.options = { loginHint: email };
    }
    await signInWithRedirect(redirectInput);
  }

  async function refreshProfile() {
    try {
      const profileData = await apiClient.get('/api/auth/profile');
      setProfile(unwrapProfile(profileData));
    } catch {
      setProfile(null);
    }
  }

  async function refreshSession() {
    await authClient.fetchAuthSession({ forceRefresh: true });
    await loadUser();
  }

  async function logout() {
    // The page the user logs out from must not become the next login's destination.
    suppressPostLoginCapture();
    await authClient.signOut();
    setUser(null);
    setIsAuthenticated(false);
    setIsPasswordSession(false);
    setProfile(null);
    try {
      sessionStorage.removeItem(SSO_WARNING_DISMISSED_KEY);
    } catch {
      /* ignore */
    }
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('banyan_assessment_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
  }

  // Role is the single source of truth for authorization, sourced from the DB
  // profile (`/api/auth/profile`). Stale JWT `cognito:groups` claims are
  // ignored by design — see TASK-078 Phase 1 (AC-P1-STALENESS-1).
  const role =
    typeof profile?.role === 'string' && profile.role ? profile.role : null;
  const flags = coerceFlags(profile?.flags);
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      isPasswordSession,
      profile,
      role,
      isAdmin: role === 'admin',
      isLeader: role === 'leader',
      isCeo: role === 'ceo',
      isTechLeader: role === 'tech_leader',
      // FEAT-067: pool-membership flag from the profile (role-agnostic — an
      // instructor can hold any base role, so this is independent of `role`).
      isInstructor: profile?.isInstructor === true,
      // Feature-flags task Phase 4.
      flags,
      oauthError,
      quarantineError,
      ssoEnabled: isSsoEnabled(),
      login,
      completeNewPassword,
      register,
      logout,
      signInWithBanyanSSO,
      confirmSignUp: confirmSignUpFn,
      forgotPassword,
      forgotPasswordSubmit,
      refreshProfile,
      refreshSession,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, isAuthenticated, isLoading, isPasswordSession, profile, role, flags, oauthError, quarantineError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
